import { useTranslation } from 'react-i18next';
import { Badge, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import {
  CAPABILITY_STATE_LABELS,
  CAPABILITY_STATE_VARIANT,
  type ConnectorCapability,
} from '@/entities/media-connector';

/**
 * Where a failing capability's reason has to be readable.
 *
 * Not a `title=` attribute and not a hover tooltip, which was the tempting shape. The
 * message that matters most is Google's, and it names the cloud project and carries the
 * console URL that fixes the problem — text you have to be able to select, and a link you
 * have to be able to click. Neither is possible in a tooltip.
 */
interface CapabilityBadgeProps {
  label: string;
  capability: ConnectorCapability;
  badgeClasses: Record<string, string>;
}

/** Bare URLs in the platform's own sentence, made clickable without altering the words. */
function linkify(message: string) {
  return message.split(/(https?:\/\/\S+)/g).map((piece, index) =>
    /^https?:\/\//.test(piece) ? (
      <a
        key={index}
        href={piece}
        target="_blank"
        rel="noreferrer noopener"
        className="break-all text-primary underline underline-offset-2"
      >
        {piece}
      </a>
    ) : (
      <span key={index}>{piece}</span>
    ),
  );
}

export function CapabilityBadge({ label, capability, badgeClasses }: CapabilityBadgeProps) {
  const { t } = useTranslation('knowledge');
  const variant = CAPABILITY_STATE_VARIANT[capability.state];
  const badge = (
    <Badge
      variant="ghost"
      roundness="full"
      className={`shrink-0 px-2 py-0.5 text-xs font-medium ${badgeClasses[variant] ?? ''}`}
    >
      {label}
    </Badge>
  );

  // Nothing to open when there is nothing to say. A badge that looks clickable and then
  // shows an empty panel is worse than one that plainly is not.
  if (capability.state !== 'failing' || !capability.error) {
    return <span title={`${label}: ${CAPABILITY_STATE_LABELS[capability.state]}`}>{badge}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer"
          aria-label={t('mediaConnectors.capabilityBadge.whyItFailed', { capability: label })}
        >
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 space-y-2">
        <p className="text-sm font-medium text-foreground">
          {t('mediaConnectors.capabilityBadge.capabilityIsFailing', { capability: label })}
        </p>
        {/* The platform's own words, verbatim and selectable. Ours would be a paraphrase
            of an error we did not write, and would drop the URL that fixes it. */}
        <p className="text-xs leading-relaxed break-words text-muted-foreground select-text">
          {linkify(capability.error)}
        </p>
        {capability.checkedAt && (
          <p className="text-xs text-muted-foreground">
            {t('mediaConnectors.capabilityBadge.lastChecked', {
              timestamp: new Date(capability.checkedAt).toLocaleString(),
            })}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
